import { resolveRouteFactories } from "../api/route.js";
import { getMountRoute } from "../api/internal/route.js";
import { RequestInputContext } from "../api/request-input-context.js";
import { RequestOutputContext } from "../api/request-output-context.js";
import { buildPath, extractPathParams } from "../api/internal/path.js";
import { FragnoClientApiError, FragnoClientError, FragnoClientFetchAbortError, FragnoClientFetchError, FragnoClientFetchNetworkError, FragnoClientUnknownApiError } from "./client-error.js";
import { parseContentType } from "../util/content-type.js";
import { handleNdjsonStreamingFirstItem } from "./internal/ndjson-streaming.js";
import { SSR_ENABLED, addStore, getInitialData } from "../util/ssr.js";
import { unwrapObject } from "../util/nanostores.js";
import { mergeFetcherConfigs } from "./internal/fetcher-merge.js";
import { computed, task } from "nanostores";
import { nanoquery } from "@nanostores/query";

//#region src/client/client.ts
/**
* Symbols used to identify hook types
*/
const GET_HOOK_SYMBOL = Symbol("fragno-get-hook");
const MUTATOR_HOOK_SYMBOL = Symbol("fragno-mutator-hook");
const STORE_SYMBOL = Symbol("fragno-store");
/**
* Check if a value contains files that should be sent as FormData.
* @internal
*/
function containsFiles(value) {
	if (value instanceof File || value instanceof Blob) return true;
	if (value instanceof FormData) return true;
	if (typeof value === "object" && value !== null) return Object.values(value).some((v) => v instanceof File || v instanceof Blob || v instanceof FormData);
	return false;
}
/**
* Convert an object containing files to FormData.
* Handles nested File/Blob values by appending them directly.
* Other values are JSON-stringified.
* @internal
*/
function toFormData(value) {
	const formData = new FormData();
	for (const [key, val] of Object.entries(value)) if (val instanceof File) formData.append(key, val, val.name);
	else if (val instanceof Blob) formData.append(key, val);
	else if (val !== void 0 && val !== null) formData.append(key, typeof val === "string" ? val : JSON.stringify(val));
	return formData;
}
/**
* Prepare request body and headers for sending.
* Handles FormData (file uploads) vs JSON data.
* @internal
*/
function prepareRequestBody(body, contentType) {
	if (body === void 0) return { body: void 0 };
	if (contentType === "application/octet-stream") {
		if (body instanceof ReadableStream || body instanceof Blob || body instanceof File || body instanceof ArrayBuffer || body instanceof Uint8Array) return {
			body,
			headers: { "Content-Type": "application/octet-stream" }
		};
		throw new Error("Octet-stream routes only accept Blob, File, ArrayBuffer, Uint8Array, or ReadableStream bodies.");
	}
	if (body instanceof FormData) return { body };
	if (body instanceof File) {
		const formData = new FormData();
		formData.append("file", body, body.name);
		return { body: formData };
	}
	if (body instanceof Blob) {
		const formData = new FormData();
		formData.append("file", body);
		return { body: formData };
	}
	if (typeof body === "object" && body !== null && containsFiles(body)) return { body: toFormData(body) };
	return {
		body: JSON.stringify(body),
		headers: { "Content-Type": "application/json" }
	};
}
/**
* Merge request headers from multiple sources.
* Returns undefined if there are no headers to merge.
* @internal
*/
function mergeRequestHeaders(...headerSources) {
	const result = {};
	let hasHeaders = false;
	for (const source of headerSources) {
		if (!source) continue;
		if (source instanceof Headers) for (const [key, value] of source.entries()) {
			result[key] = value;
			hasHeaders = true;
		}
		else if (Array.isArray(source)) for (const [key, value] of source) {
			result[key] = value;
			hasHeaders = true;
		}
		else for (const [key, value] of Object.entries(source)) {
			result[key] = value;
			hasHeaders = true;
		}
	}
	return hasHeaders ? result : void 0;
}
/**
* @internal
*/
function buildUrl(config, params) {
	const { baseUrl = "", mountRoute, path } = config;
	const { pathParams, queryParams } = params ?? {};
	const normalizedPathParams = unwrapObject(pathParams);
	const normalizedQueryParams = unwrapObject(queryParams) ?? {};
	const filteredQueryParams = Object.fromEntries(Object.entries(normalizedQueryParams).filter(([_, value]) => value !== void 0));
	const searchParams = new URLSearchParams(filteredQueryParams);
	return `${baseUrl}${mountRoute}${buildPath(path, normalizedPathParams ?? {})}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
}
/**
* This method returns an array, which can be passed directly to nanostores.
*
* The returned array is always: path, pathParams (In order they appear in the path), queryParams (In alphabetical order)
* Missing pathParams are replaced with "<missing>".
* Atoms with undefined values are wrapped in computed atoms that map undefined to "" to avoid nanoquery treating the key as incomplete.
* @param path
* @param params
* @returns
* @internal
*/
function getCacheKey(method, path, params) {
	if (!params) return [method, path];
	const { pathParams, queryParams } = params;
	const pathParamValues = extractPathParams(path).map((name) => pathParams?.[name] ?? "<missing>");
	const queryParamValues = queryParams ? Object.keys(queryParams).sort().map((key) => {
		const value = queryParams[key];
		if (value && typeof value === "object" && "get" in value) return computed(value, (v) => v ?? "");
		return value ?? "";
	}) : [];
	return [
		method,
		path,
		...pathParamValues,
		...queryParamValues
	];
}
function isStreamingResponse(response) {
	const contentType = parseContentType(response.headers.get("content-type"));
	if (!contentType) return false;
	if (!(response.headers.get("transfer-encoding") === "chunked")) return false;
	if (contentType.subtype === "octet-stream") return "octet-stream";
	if (contentType.subtype === "x-ndjson") return "ndjson";
	return false;
}
/**
* @internal
*/
function isGetHook(hook) {
	return typeof hook === "object" && hook !== null && GET_HOOK_SYMBOL in hook && hook[GET_HOOK_SYMBOL] === true;
}
/**
* @internal
*/
function isMutatorHook(hook) {
	return typeof hook === "object" && hook !== null && MUTATOR_HOOK_SYMBOL in hook && hook[MUTATOR_HOOK_SYMBOL] === true;
}
/**
* @internal
*/
function isStore(obj) {
	return typeof obj === "object" && obj !== null && STORE_SYMBOL in obj && obj[STORE_SYMBOL] === true;
}
var ClientBuilder = class {
	#publicConfig;
	#fragmentConfig;
	#fetcherConfig;
	#cache = /* @__PURE__ */ new Map();
	#createFetcherStore;
	#createMutatorStore;
	#invalidateKeys;
	constructor(publicConfig, fragmentConfig) {
		this.#publicConfig = publicConfig;
		this.#fragmentConfig = fragmentConfig;
		this.#fetcherConfig = publicConfig.fetcherConfig;
		const [createFetcherStore, createMutatorStore, { invalidateKeys }] = nanoquery({ cache: this.#cache });
		this.#createFetcherStore = createFetcherStore;
		this.#createMutatorStore = createMutatorStore;
		this.#invalidateKeys = invalidateKeys;
	}
	get cacheEntries() {
		return Object.fromEntries(this.#cache.entries());
	}
	createStore(obj) {
		return {
			obj,
			[STORE_SYMBOL]: true
		};
	}
	/**
	* Build a URL for a custom backend call using the configured baseUrl and mountRoute.
	* Useful for fragment authors who need to make custom fetch calls.
	*/
	buildUrl(path, params) {
		return buildUrl({
			baseUrl: this.#publicConfig.baseUrl ?? "",
			mountRoute: getMountRoute({
				name: this.#fragmentConfig.name,
				mountRoute: this.#publicConfig.mountRoute
			}),
			path
		}, {
			pathParams: params?.path,
			queryParams: params?.query
		});
	}
	/**
	* Get the configured fetcher function for custom backend calls.
	* Returns fetch with merged options applied.
	*/
	getFetcher() {
		return {
			fetcher: this.#getFetcher(),
			defaultOptions: this.#getFetcherOptions()
		};
	}
	#getFetcher() {
		if (this.#fetcherConfig?.type === "function") return this.#fetcherConfig.fetcher;
		return fetch;
	}
	#getFetcherOptions() {
		if (this.#fetcherConfig?.type === "options") return this.#fetcherConfig.options;
	}
	createHook(path, options) {
		const route = this.#fragmentConfig.routes.find((r) => r.path === path && r.method === "GET" && r.outputSchema !== void 0);
		if (!route) throw new Error(`Route '${path}' not found or is not a GET route with an output schema.`);
		return this.#createRouteQueryHook(route, options);
	}
	createMutator(method, path, onInvalidate) {
		const route = this.#fragmentConfig.routes.find((r) => r.method !== "GET" && r.path === path && r.method === method);
		if (!route) throw new Error(`Route '${path}' not found or is a GET route with an input and output schema.`);
		return this.#createRouteQueryMutator(route, onInvalidate);
	}
	#createRouteQueryHook(route, options = {}) {
		if (route.method !== "GET") throw new Error(`Only GET routes are supported for hooks. Route '${route.path}' is a ${route.method} route.`);
		if (!route.outputSchema) throw new Error(`Output schema is required for GET routes. Route '${route.path}' has no output schema.`);
		const baseUrl = this.#publicConfig.baseUrl ?? "";
		const mountRoute = getMountRoute({
			name: this.#fragmentConfig.name,
			mountRoute: this.#publicConfig.mountRoute
		});
		const fetcher = this.#getFetcher();
		const fetcherOptions = this.#getFetcherOptions();
		async function callServerSideHandler(params) {
			const { pathParams, queryParams } = params ?? {};
			const normalizedPathParams = unwrapObject(pathParams);
			const normalizedQueryParams = unwrapObject(queryParams) ?? {};
			const filteredQueryParams = Object.fromEntries(Object.entries(normalizedQueryParams).filter(([_, value]) => value !== void 0));
			const searchParams = new URLSearchParams(filteredQueryParams);
			return await route.handler(RequestInputContext.fromSSRContext({
				method: route.method,
				path: route.path,
				pathParams: normalizedPathParams,
				searchParams
			}), new RequestOutputContext(route.outputSchema));
		}
		async function executeQuery(params) {
			const { pathParams, queryParams } = params ?? {};
			if (typeof window === "undefined") return task(async () => callServerSideHandler({
				pathParams,
				queryParams
			}));
			const url = buildUrl({
				baseUrl,
				mountRoute,
				path: route.path
			}, {
				pathParams,
				queryParams
			});
			let response;
			try {
				response = fetcherOptions ? await fetcher(url, fetcherOptions) : await fetcher(url);
			} catch (error) {
				throw FragnoClientFetchError.fromUnknownFetchError(error);
			}
			if (!response.ok) throw await FragnoClientApiError.fromResponse(response);
			return response;
		}
		return {
			route,
			store: (args) => {
				const { path, query } = args ?? {};
				const key = getCacheKey(route.method, route.path, {
					pathParams: path,
					queryParams: query
				});
				const store = this.#createFetcherStore(key, {
					fetcher: async () => {
						if (SSR_ENABLED) {
							const initialData = getInitialData(key.map((d) => typeof d === "string" ? d : d.get()).join(""));
							if (initialData) return initialData;
						}
						const response = await executeQuery({
							pathParams: path,
							queryParams: query
						});
						const isStreaming = isStreamingResponse(response);
						if (!isStreaming) return response.json();
						if (typeof window === "undefined") return [];
						if (isStreaming === "ndjson") {
							const { firstItem } = await handleNdjsonStreamingFirstItem(response, {
								setData: (value) => {
									store.set({
										...store.get(),
										loading: !(Array.isArray(value) && value.length > 0),
										data: value
									});
								},
								setError: (value) => {
									store.set({
										...store.get(),
										error: value
									});
								}
							});
							return [firstItem];
						}
						if (isStreaming === "octet-stream") throw new Error("Octet-stream streaming is not supported.");
						throw new Error("Unreachable");
					},
					onErrorRetry: options?.onErrorRetry,
					dedupeTime: Infinity
				});
				if (typeof window === "undefined") addStore(store);
				return store;
			},
			query: async (args) => {
				const { path, query } = args ?? {};
				const response = await executeQuery({
					pathParams: path,
					queryParams: query
				});
				const isStreaming = isStreamingResponse(response);
				if (!isStreaming) return await response.json();
				if (isStreaming === "ndjson") {
					const { streamingPromise } = await handleNdjsonStreamingFirstItem(response);
					return await streamingPromise;
				}
				if (isStreaming === "octet-stream") throw new Error("Octet-stream streaming is not supported.");
				throw new Error("Unreachable");
			},
			[GET_HOOK_SYMBOL]: true
		};
	}
	#createRouteQueryMutator(route, onInvalidate = (invalidate, params) => invalidate("GET", route.path, params)) {
		const method = route.method;
		const baseUrl = this.#publicConfig.baseUrl ?? "";
		const mountRoute = getMountRoute({
			name: this.#fragmentConfig.name,
			mountRoute: this.#publicConfig.mountRoute
		});
		const fetcher = this.#getFetcher();
		const fetcherOptions = this.#getFetcherOptions();
		async function executeMutateQuery({ body, path, query }) {
			if (typeof window === "undefined") return task(async () => route.handler(RequestInputContext.fromSSRContext({
				inputSchema: route.inputSchema,
				method,
				path: route.path,
				pathParams: path ?? {},
				searchParams: new URLSearchParams(query),
				body
			}), new RequestOutputContext(route.outputSchema)));
			const url = buildUrl({
				baseUrl,
				mountRoute,
				path: route.path
			}, {
				pathParams: path,
				queryParams: query
			});
			let response;
			try {
				const { body: preparedBody, headers: bodyHeaders } = prepareRequestBody(body, route.contentType);
				const mergedHeaders = mergeRequestHeaders(fetcherOptions?.headers, bodyHeaders);
				const requestOptions = {
					...fetcherOptions,
					method,
					body: preparedBody,
					...mergedHeaders ? { headers: mergedHeaders } : {}
				};
				if (preparedBody instanceof ReadableStream) requestOptions.duplex = "half";
				response = await fetcher(url, requestOptions);
			} catch (error) {
				throw FragnoClientFetchError.fromUnknownFetchError(error);
			}
			if (!response.ok) throw await FragnoClientApiError.fromResponse(response);
			return response;
		}
		const mutatorStore = this.#createMutatorStore(async ({ data }) => {
			if (typeof window === "undefined") {}
			const { body, path, query } = data;
			if (typeof body === "undefined" && route.inputSchema !== void 0) throw new Error("Body is required.");
			const response = await executeMutateQuery({
				body,
				path,
				query
			});
			onInvalidate(this.#invalidate.bind(this), {
				pathParams: path ?? {},
				queryParams: query
			});
			if (response.status === 201 || response.status === 204) return;
			const isStreaming = isStreamingResponse(response);
			if (!isStreaming) return response.json();
			if (typeof window === "undefined") return [];
			if (isStreaming === "ndjson") {
				const { firstItem } = await handleNdjsonStreamingFirstItem(response, {
					setData: (value) => {
						mutatorStore.set({
							...mutatorStore.get(),
							loading: !(Array.isArray(value) && value.length > 0),
							data: value
						});
					},
					setError: (value) => {
						mutatorStore.set({
							...mutatorStore.get(),
							error: value
						});
					}
				});
				return [firstItem];
			}
			if (isStreaming === "octet-stream") throw new Error("Octet-stream streaming is not supported.");
			throw new Error("Unreachable");
		}, { onError: (error) => {
			console.error("Error in mutatorStore", error);
		} });
		const mutateQuery = (async (data) => {
			const { body, path, query } = data;
			if (typeof body === "undefined" && route.inputSchema !== void 0) throw new Error("Body is required for mutateQuery");
			const response = await executeMutateQuery({
				body,
				path,
				query
			});
			if (response.status === 201 || response.status === 204) return;
			const isStreaming = isStreamingResponse(response);
			if (!isStreaming) return response.json();
			if (isStreaming === "ndjson") {
				const { streamingPromise } = await handleNdjsonStreamingFirstItem(response);
				return await streamingPromise;
			}
			if (isStreaming === "octet-stream") throw new Error("Octet-stream streaming is not supported for mutations");
			throw new Error("Unreachable");
		});
		return {
			route,
			mutateQuery,
			mutatorStore,
			[MUTATOR_HOOK_SYMBOL]: true
		};
	}
	#invalidate(method, path, params) {
		const prefix = getCacheKey(method, path, {
			pathParams: params?.pathParams,
			queryParams: params?.queryParams
		}).map((k) => typeof k === "string" ? k : k.get()).join("");
		this.#invalidateKeys((key) => key.startsWith(prefix));
	}
};
/**
* Create a client builder for fragments using the new fragment definition API.
* This is the same as createClientBuilder but works with FragmentDefinition.
*/
function createClientBuilder(definition, publicConfig, routesOrFactories, authorFetcherConfig) {
	const routes = resolveRouteFactories({
		config: {},
		deps: {},
		services: {},
		serviceDeps: {}
	}, routesOrFactories);
	const fragmentConfig = {
		name: definition.name,
		routes
	};
	const mountRoute = getMountRoute({
		name: definition.name,
		mountRoute: publicConfig.mountRoute
	});
	const mergedFetcherConfig = mergeFetcherConfigs(authorFetcherConfig, publicConfig.fetcherConfig);
	return new ClientBuilder({
		...publicConfig,
		mountRoute,
		fetcherConfig: mergedFetcherConfig
	}, fragmentConfig);
}

//#endregion
export { ClientBuilder, FragnoClientApiError, FragnoClientError, FragnoClientFetchAbortError, FragnoClientFetchError, FragnoClientFetchNetworkError, FragnoClientUnknownApiError, buildUrl, createClientBuilder, getCacheKey, isGetHook, isMutatorHook, isStore };
//# sourceMappingURL=client.js.map